import { ReachAPI } from '../ReachApi';
import { supportsVideoAssetCache, platform, NotImplementedError } from '../Platform';

/**
 * Sleep for a given duration in milliseconds.
 * @private
 * @param duration - The time, in milliseconds, the timer should wait before the
 * promise is resolved.
 */
async function sleep(duration: number): Promise<void> {
    await new Promise(function (resolve) {
        setTimeout(resolve, duration);
    });
}

/**
 * Await the download of an asset
 * @private
 */
async function awaitDownload(assetUuid: string): Promise<void> {
    const thePlatform = platform();
    if (!supportsVideoAssetCache(thePlatform)) {
        throw new NotImplementedError('VideoAssetCache is not supported on this platform');
    }

    let isCached = false;
    while (!isCached) {
        isCached = await thePlatform.isCachedAsset(assetUuid);
        if (!isCached) {
            await sleep(1000);
        }
    }
}

/**
 * Return cached video path after download is complete
 * @private
 */
async function getCachedVideoPath(assetUuid: string): Promise<string> {
    const thePlatform = platform();
    if (!supportsVideoAssetCache(thePlatform)) {
        throw new NotImplementedError('VideoAssetCache is not supported on this platform');
    }

    const isCached = await thePlatform.cacheVideoAsset(assetUuid);
    if (!isCached) {
        await awaitDownload(assetUuid);
    }
    const path = await thePlatform.getVideoPath(assetUuid);
    return path;
}

/**
 * Get API path to asset.
 */
export function getGenericPath(assetUuid: string): string {
    return ReachAPI.server + '/cdn/asset/' + assetUuid;
}

/**
 * Get path to cached asset, if caching is supported by the platform. Otherwise
 * the API path will be returned. This will wait until the asset has been cached
 * before resolving.
 */
export async function getPath(assetUuid: string): Promise<string> {
    const thePlatform = platform();
    let path = getGenericPath(assetUuid);
    if (supportsVideoAssetCache(thePlatform)) {
        path = await getCachedVideoPath(assetUuid);
    }
    return path;
}

/**
 * Get path to cached asset for immediate use. Start cache process if not
 * cached, but do not wait for it to finish. If caching is not supported by the
 * platform, the API path will be returned.
 */
export async function getPathImmediately(assetUuid: string): Promise<string> {
    const thePlatform = platform();
    let path = getGenericPath(assetUuid);
    if (supportsVideoAssetCache(thePlatform)) {
        await thePlatform.cacheVideoAsset(assetUuid);
        path = await thePlatform.getVideoPath(assetUuid);
    }
    return path;
}
